import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, API, formatApiError } from "../lib/api";
import { Database, Download, Upload, AlertTriangle, Loader2, CheckCircle2 } from "lucide-react";

/**
 * BackupSection — admin-only. Two flows:
 *
 *   1. Download a portable snapshot (.zip of per-collection JSON + a
 *      `_manifest.json`) so the whole database can be moved to a new
 *      server.
 *
 *   2. Restore from a previously-downloaded .zip. Merge mode: upserts by
 *      the application-level `id` field; docs without `id` are
 *      insert-only with duplicate-key collisions silently skipped.
 *
 * A pre-download status check (`GET /api/settings/backup/status`) gives
 * the operator collection counts + total document count so they know
 * what they're about to download before hitting a slow link.
 */
export default function BackupSection() {
  const [status, setStatus] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const inputRef = useRef(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/settings/backup/status");
      setStatus(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not read backup status");
    }
  };
  useEffect(() => { refresh(); }, []);

  const doDownload = async () => {
    setDownloading(true);
    try {
      // Fetch with credentials so the httpOnly session cookie is sent.
      // Streams the whole zip into a Blob then triggers a synthetic
      // <a download> click — same UX as a native download.
      const res = await fetch(`${API}/settings/backup/download`, {
        method: "GET",
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      // Prefer the server-provided filename from Content-Disposition.
      const disp = res.headers.get("Content-Disposition") || "";
      const match = disp.match(/filename="([^"]+)"/);
      a.href = url;
      a.download = match ? match[1] : `solaros-backup-${Date.now()}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (e) {
      toast.error(e.message || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const doRestore = async () => {
    if (!pendingFile) return;
    setRestoring(true);
    setRestoreResult(null);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      const { data } = await api.post("/settings/backup/restore", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRestoreResult(data);
      toast.success(`Restore complete — ${data.totals.upserted} rows updated, ${data.totals.inserted} new`);
      setPendingFile(null);
      setConfirmRestore(false);
      if (inputRef.current) inputRef.current.value = "";
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Restore failed");
    } finally {
      setRestoring(false);
    }
  };

  const kb = (b) => b == null ? "—" : b >= 1024 ? `${(b / 1024).toFixed(1)} KB` : `${b} B`;
  return (
    <section className="swiss-card-flat p-6 mb-6" data-testid="backup-section">
      <div className="flex items-center gap-2 mb-4">
        <Database size={14} />
        <div className="label">Database backup &amp; restore (admin only)</div>
      </div>
      <p className="muted text-xs mb-4">
        Portable snapshot of every collection — use this to move the whole app to a new server or take a monthly offline archive.
        Restore is <strong>merge-mode</strong>: docs with the same <code className="font-mono">id</code> are updated in place, new docs are inserted, duplicate rows (e.g. monitoring samples with a compound unique index) are safely skipped.
      </p>

      {/* Status card */}
      {status && (
        <div className="mb-4 p-4 rounded-lg bg-[var(--surface-2,#F5F5F4)] border border-[var(--line)]"
             data-testid="backup-status-card">
          <div className="grid grid-cols-3 gap-4 mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider muted font-semibold">Database</div>
              <div className="font-mono text-sm font-semibold" data-testid="backup-status-db">{status.db_name}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider muted font-semibold">Collections</div>
              <div className="font-mono text-sm font-semibold" data-testid="backup-status-collections">{status.collection_count}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider muted font-semibold">Total documents</div>
              <div className="font-mono text-sm font-semibold" data-testid="backup-status-docs">
                {(status.total_documents || 0).toLocaleString("en-GB")}
              </div>
            </div>
          </div>
          <details className="text-xs">
            <summary className="cursor-pointer muted hover:text-[var(--ink)]">
              Per-collection counts ({status.collections.length})
            </summary>
            <table className="w-full mt-2 text-xs font-mono">
              <tbody>
                {status.collections.map(c => (
                  <tr key={c.name} data-testid={`backup-row-${c.name}`}>
                    <td className="py-0.5">{c.name}</td>
                    <td className="text-right">{c.documents.toLocaleString("en-GB")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        </div>
      )}

      {/* Download */}
      <div className="mb-6 p-4 border border-[var(--line)] rounded-lg">
        <div className="flex items-start gap-4">
          <Download size={20} className="text-[var(--accent-hover)] mt-1" />
          <div className="flex-1">
            <div className="font-semibold text-sm mb-1">Download backup</div>
            <div className="text-xs muted mb-3">
              Streams a <code className="font-mono">.zip</code> containing one JSON file per collection + a <code className="font-mono">_manifest.json</code>. Includes every collection including audit logs.
            </div>
            <button
              onClick={doDownload}
              disabled={downloading}
              className="btn-primary"
              data-testid="backup-download-btn"
            >
              {downloading ? <><Loader2 size={13} className="animate-spin" /> Preparing…</> : <><Download size={13} /> Download backup .zip</>}
            </button>
          </div>
        </div>
      </div>

      {/* Restore */}
      <div className="p-4 border-2 rounded-lg" style={{ borderColor: "#B33C8655", background: "#F7DCEC20" }}>
        <div className="flex items-start gap-4">
          <Upload size={20} style={{ color: "#B8721E" }} className="mt-1" />
          <div className="flex-1">
            <div className="font-semibold text-sm mb-1">Restore from backup</div>
            <div className="text-xs muted mb-3">
              Upload a <code className="font-mono">.zip</code> produced by the button above. Existing docs will be <strong>updated in place</strong> — anything newer on this server that isn&apos;t in the backup is preserved.
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                ref={inputRef}
                type="file"
                accept=".zip,application/zip"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  setPendingFile(f || null);
                  setConfirmRestore(false);
                  setRestoreResult(null);
                }}
                className="text-xs"
                data-testid="backup-restore-file"
              />
              {pendingFile && (
                <div className="text-xs font-mono muted" data-testid="backup-file-info">
                  {pendingFile.name} · {kb(pendingFile.size)}
                </div>
              )}
            </div>
            {pendingFile && !confirmRestore && (
              <div className="mt-3 flex items-center gap-2">
                <button
                  onClick={() => setConfirmRestore(true)}
                  className="text-xs px-3 py-2 rounded border border-[#B8721E] text-[#B8721E] font-semibold hover:bg-[#F7DCEC]"
                  data-testid="backup-restore-review"
                >
                  <AlertTriangle size={12} className="inline mr-1" />
                  Review &amp; confirm
                </button>
              </div>
            )}
            {confirmRestore && (
              <div className="mt-3 p-3 rounded bg-white border border-[#B8721E]"
                   data-testid="backup-restore-confirm-panel">
                <div className="text-xs mb-3 flex items-start gap-2">
                  <AlertTriangle size={13} style={{ color: "#B8721E" }} className="mt-0.5 shrink-0" />
                  <div>
                    <strong>Are you sure?</strong> Every doc with a matching <code className="font-mono">id</code> in your live database will be overwritten with the version from the backup. New docs will be added. This can&apos;t be undone from the UI — take a fresh download before proceeding if you might need to roll back.
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={doRestore}
                    disabled={restoring}
                    className="btn-primary"
                    data-testid="backup-restore-confirm"
                  >
                    {restoring
                      ? <><Loader2 size={13} className="animate-spin" /> Restoring…</>
                      : <><Upload size={13} /> Restore now</>}
                  </button>
                  <button
                    onClick={() => { setConfirmRestore(false); setPendingFile(null); if (inputRef.current) inputRef.current.value = ""; }}
                    disabled={restoring}
                    className="btn-ghost"
                    data-testid="backup-restore-cancel"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            {restoreResult && (
              <div className="mt-3 p-3 rounded bg-[#DDF3F1] border border-[#0F8B84]"
                   data-testid="backup-restore-result">
                <div className="flex items-center gap-2 text-sm font-semibold text-[#0F8B84] mb-2">
                  <CheckCircle2 size={14} /> Restore complete
                </div>
                <div className="text-xs muted grid grid-cols-3 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-semibold">Upserted</div>
                    <div className="font-mono text-sm text-[var(--ink)]" data-testid="backup-restore-upserted">
                      {restoreResult.totals.upserted.toLocaleString("en-GB")}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-semibold">Inserted</div>
                    <div className="font-mono text-sm text-[var(--ink)]" data-testid="backup-restore-inserted">
                      {restoreResult.totals.inserted.toLocaleString("en-GB")}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-semibold">Skipped (dupe)</div>
                    <div className="font-mono text-sm text-[var(--ink)]" data-testid="backup-restore-skipped">
                      {restoreResult.totals.skipped.toLocaleString("en-GB")}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
